import {decorate, observable} from "mobx";
import {
    buildFixedValuesWaveform,
    refreshSampleHold,
    RegenWaveFunction,
    WAVEFORMS,
    WaveFunction
} from "../waveforms";
import midi from "./MidiStore";

export type MessageType = "CC" | "NRPN";

export const CUSTOM_ORDER_DEFAULT = null;
export const CUSTOM_ORDER_ASC = "A";
export const CUSTOM_ORDER_DESC = "D";
export const CUSTOM_ORDER_RANDOM = "R";

export interface IModulator {
    name: string;           // "CC<CC>" by default
    fName: string;
    freq: number;
    phase: number;      // (0...180; 0 by default)
    messageType: MessageType,
    messageNumber: number;
    channel: number;
    range: number;
    min: number;
    max: number;
    customValues: string;   // empty values means random
    customOrder: null|string;
    linked: boolean;
}

//TODO: add regenerateWave property for force a new "SH" wave for example

export class Modulator implements IModulator {
    fName: string;
    messageType: MessageType;
    messageNumber: number;
    channel: number;
    freq: number;
    max: number;
    min: number;
    name: string;
    range: number;
    phase: number;      // (0...180; 0 by default)
    linked: boolean;
    running: boolean;
    regenWave: undefined|RegenWaveFunction;  // if defined, will be call at the end of each cycle
    regenCounter: number;   // to force a refresh of the Wave form on screen
    waveSamples: number[];    // contains the wave values (-1..+1) if the wave can not be defined by a function (e.g. SampleAndHold)
    waveSamplesCustom: number[];    // contains the wave values (0..127) if the wave can not be defined by a function (e.g. SampleAndHold)
    customValues: string;
    customOrder: null|string;
    muted = false;
    f: WaveFunction;
    tickPeriod: number;         // [ms]
    timestamp: number;
    x: number;
    y: number;          // raw output, 0..1
    prevX: number;      // used to trigger the wave regeneration
    output: number;     // computed value ready for sending to MIDI device
    amplitude: number;  // (0...1; 1 by default)        COMPUTED from min and max
    offset: number;     // (-1...1; 0.5 by default)     COMPUTED from min and max

    //TODO: add autoStart feature

    constructor({channel, fName, freq, max, messageNumber, messageType, min, name, phase, range, customValues, customOrder, linked}: IModulator) {
        this.name = name;
        this.running = false;
        this.fName = fName;
        this.f = (x: number) => 0;
        this.x = 0;
        this.prevX = 0; // used to trigger the wave regeneration
        this.freq = freq;
        this.tickPeriod = 1000 / freq / 360;  // [ms]
        this.timestamp = -1;
        this.output = 0;
        this.channel = channel;
        this.messageType = messageType;
        this.messageNumber = messageNumber;
        this.range = range;
        this.min = min;
        this.max = max;
        this.phase = phase;
        this.amplitude = (max - min) / range;
        this.offset = ((min + max) / 2) / range;
        this.y = this.fx(this.x + this.phase);
        this.output = Math.round(this.y * this.range);
        this.linked = linked;
        this.regenWave = undefined;
        this.regenCounter = 0;
        this.waveSamples = [];
        this.waveSamplesCustom = [];
        this.customValues = customValues;
        this.customOrder = customOrder;
        this.setF(fName);
    }

    getInterfaceValues(): IModulator {
        return {
            name: this.name,
            fName: this.fName,
            freq: this.freq,
            phase: this.phase,
            messageType: this.messageType,
            messageNumber: this.messageNumber,
            channel: this.channel,
            range: this.range,
            max: this.max,
            min: this.min,
            linked: this.linked,
            customValues: this.customValues,
            customOrder: this.customOrder
        }
    }

    setFreq(freq: number) {
        this.freq = freq;
        this.tickPeriod = 1000 / freq / 360;  // [ms]
    }

    getFreq() {     // for non-reactive freq usage
        return this.freq;
    }

    setOffset(offset: number) {
        this.offset = offset;
        this.min = (this.offset - (this.amplitude / 2)) * this.range;
        this.max = (this.offset + (this.amplitude / 2)) * this.range;
        this.y = this.fx(this.x + this.phase);
        this.output = Math.round(this.y * this.range);
    }

    setOffsetMin() {
        this.setOffset(this.amplitude / 2);
    }

    setOffsetMax() {
        this.setOffset(1 - this.amplitude / 2);
    }

    setAmplitude(amplitude: number) {
        this.min = (this.offset - (amplitude / 2)) * this.range;
        this.max = (this.offset + (amplitude / 2)) * this.range;
        this.amplitude = (this.max - this.min) / this.range;
        this.offset = ((this.min + this.max) / 2) / this.range;
        this.y = this.fx(this.x + this.phase);
        this.output = Math.round(this.y * this.range);
    }

    refreshWave(): void {
        if (this.customValues && this.customValues.length > 0) {
            this.waveSamplesCustom = buildFixedValuesWaveform(this.customValues, this.customOrder);
            this.waveSamples = this.waveSamplesCustom.map(v => 2 * v / 127 - 1);
        } else {
            this.waveSamplesCustom = [];
            this.waveSamples = refreshSampleHold();
        }
        this.y = this.fx(this.x + this.phase);          // 0..1
        this.output = Math.round(this.y * this.range);
    }

    /**
     *
     */
    updateOutput() {
        this.y = this.fx(this.x + this.phase);
        // MIDI value:
        if (this.fName === "sampleHold" && this.customValues && this.customValues.length > 0) {
            this.output = this.waveSamplesCustom[Math.floor(this.x + this.phase)];
        } else {
            this.output = Math.round(this.y * this.range);
        }
    }

    /**
     * Define the function y=f(x) for the current waveform
     * f : -1..1
     * @param fName
     */
    setF(fName: string) {
        this.fName = fName;
        if (this.fName === "sampleHold") {
            this.f = (x: number) => this.waveSamples[Math.floor(x)];
            this.refreshWave();
            this.regenWave = () => {
                this.refreshWave();
                this.regenCounter++;
            };
        } else {
            this.f = WAVEFORMS[fName].f;
            this.regenWave = undefined;
        }
        this.updateOutput();
    }

    /**
     * Returns the bounded Y value for X
     * @param x
     * @return y value between 0..1
     */
    fx(x: number) {
        return Math.min(Math.max((this.amplitude / 2) * this.f(x) + this.offset, 0.0), 1.0);
    }

    /**
     *
     * @param time
     */
    tick(time: number) {

        if (!this.running) return;

        if (this.timestamp < 0) this.timestamp = time;
        const elapsed = time - this.timestamp;

        // if enough time has elapsed, compute the next value
        if (elapsed > this.tickPeriod) {
            this.timestamp = time - (elapsed % this.tickPeriod);

            this.x = (this.x + elapsed / this.tickPeriod) % 360;
            if (this.regenWave) {
                if (this.x < this.prevX) {
                    this.regenWave();
                    this.regenCounter++;
                }
            }

            this.prevX = this.x;
            this.updateOutput();

            if (!this.muted) {
                if (this.messageType === "CC") {
                    midi.sendCC(this.messageNumber, this.channel, this.output);
                } else {
                    //console.log("TODO: implement send not CC")
                }
            }
        }
    }

    fxMin() {
        return Math.min(Math.max((this.amplitude / 2) * (-1) + this.offset, 0.0), 1.0);
    }

    fxMax() {
        return Math.min(Math.max((this.amplitude / 2) + this.offset, 0.0), 1.0);
    }

    fxMid() {
        return Math.min(Math.max(this.offset, 0.0), 1.0);
    }

    sync(x: number) {
        this.timestamp = -1;
        this.x = x;
        this.y = this.fx(this.x + this.phase);
        this.output = Math.round(this.y * this.range);
    }

    reset() {
        this.timestamp = -1;
        this.x = 0;
        this.y = this.fx(this.x + this.phase);
        this.output = Math.round(this.y * this.range);
    }

    play() {
        if (this.running) return;
        // if (this.resetBeforePlay) {
        //     this.reset();
        // }
        this.timestamp = -1;
        this.running = true;
    }

    pause() {
        this.running = false;
        // this.resetBeforePlay = false;
    }

    stop() {
        this.running = false;
        // this.resetBeforePlay = true;
        this.reset();
    }

    mute() {
        this.muted = true;
    }

    unmute() {
        this.muted = false;
    }

    setLinked() {
        this.linked = true;
    }

    setUnlinked() {
        this.linked = false;
    }

    linkTo(master: IModulator) {
        this.linked = true;
        this.freq = master.freq;
        // this.phase = master.phase;
        // this.range = master.range;
        // this.max = max.range;
        // this.min = min.range;
    }

    unlink() {
        this.linked = false;
    }

    isCustomValues() {
        return this.fName === "sampleHold" && this.customValues && this.customValues.length > 0;
    }

    setCustomValues(values: string) {
        this.customValues = values;
        if (this.regenWave) this.regenWave();
    }

    setCustomOrder(order: null|string) {
        this.customOrder = order;
        if (this.regenWave) this.regenWave();
    }

}

decorate(Modulator, {
    name: observable,
    messageNumber: observable,
    messageType: observable,
    channel: observable,
    fName: observable,
    f: observable,
    freq: observable,
    // freqRange: observable,
    phase: observable,
    tickPeriod: observable,
    running: observable,
    muted: observable,
    x: observable,
    y: observable,
    amplitude: observable,
    offset: observable,
    output: observable,
    linked: observable,
    regenWave: observable,
    regenCounter: observable,
    customValues: observable,
    customOrder: observable
});
